import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAutoSave — shared automatic-save engine used across student submission
 * screens and every teacher editor (assignments, exercises, review feedback).
 *
 * Design goals (Automatic Save System spec):
 *   • "Google-Docs" experience — the user never clicks Save.
 *   • Debounced text saving (~2.5s) that never fires mid-keystroke.
 *   • Immediate saving for discrete actions (title, dropdowns, checkboxes,
 *     comments, AI-suggestion accept/dismiss, highlight colour, review settings…).
 *   • Only changed fields are transmitted (field-level diffing → PATCH-friendly).
 *   • No duplicate requests, no overlapping requests.
 *   • Offline-safe: pending changes are preserved in localStorage and retried
 *     automatically when connectivity returns.
 *   • Auto-save ONLY persists data — the caller's onSave must never call AI.
 *
 * Status values: "idle" | "saving" | "saved" | "error" | "retrying".
 * The AutoSaveIndicator component maps these to the user-facing wording.
 *
 * @param {object} data  Object whose top-level keys are the persisted fields.
 * @param {(changed:object, meta:{full:object, immediate:boolean})=>Promise<any>} onSave
 *        Persists the given (changed-only) fields. Reject to signal failure.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]      Master switch.
 * @param {number}  [options.debounceMs=2500]   Idle delay before a text save.
 * @param {number}  [options.maxWaitMs=30000]   Ceiling between saves while typing continuously.
 * @param {(v:any)=>string} [options.serialize=JSON.stringify]  Per-field change detector.
 * @param {(full:object)=>boolean} [options.canSave]  Guard; return false to skip.
 * @param {string}  [options.storageKey]        localStorage key for offline persistence.
 * @param {boolean} [options.saveOnLifecycle=false]  Flush on blur / tab-hide / pagehide /
 *        beforeunload / network-restored (used for student writing).
 * @returns {{ status, lastSavedAt, isOnline, hasPendingChanges,
 *             saveNow:()=>Promise<void>, saveImmediately:(patch?:object)=>Promise<void>,
 *             markSaved:(data:object)=>void }}
 */
export default function useAutoSave(data, onSave, options = {}) {
  const {
    enabled = true,
    debounceMs = 2500,
    maxWaitMs = 30000,
    serialize = JSON.stringify,
    canSave,
    storageKey,
    saveOnLifecycle = false,
  } = options;

  const [status, setStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  // Latest values kept in refs so timer/event callbacks never go stale and the
  // effects don't need to re-register on every keystroke.
  const dataRef = useRef(data);
  const onSaveRef = useRef(onSave);
  const canSaveRef = useRef(canSave);
  const optionRef = useRef({ serialize, storageKey, debounceMs, maxWaitMs });
  // Per-field serialized snapshot of the last successfully persisted values.
  const savedFieldsRef = useRef({});
  const isSavingRef = useRef(false);
  const pendingRef = useRef(false); // newer change arrived during a save
  const retryCountRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    onSaveRef.current = onSave;
    canSaveRef.current = canSave;
    optionRef.current = { serialize, storageKey, debounceMs, maxWaitMs };
  });

  const clearTimers = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (maxWaitTimerRef.current) clearTimeout(maxWaitTimerRef.current);
    debounceTimerRef.current = null;
    maxWaitTimerRef.current = null;
  }, []);

  /** Compute the subset of fields whose value differs from the saved baseline. */
  const getChangedFields = useCallback((obj) => {
    const { serialize: ser } = optionRef.current;
    const changed = {};
    Object.keys(obj || {}).forEach((key) => {
      if (ser(obj[key]) !== savedFieldsRef.current[key]) changed[key] = obj[key];
    });
    return changed;
  }, []);

  /** Seed the baseline without saving — call after loading server/draft data so
   *  an untouched form is never treated as dirty. */
  const markSaved = useCallback((value) => {
    const { serialize: ser } = optionRef.current;
    const map = {};
    Object.keys(value || {}).forEach((key) => {
      map[key] = ser(value[key]);
    });
    savedFieldsRef.current = map;
    pendingRef.current = false;
  }, []);

  const persistLocally = useCallback((obj) => {
    const { storageKey: key } = optionRef.current;
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({ data: obj, ts: Date.now() }));
    } catch (e) {
      /* storage full / unavailable — best effort only */
    }
  }, []);

  const clearLocal = useCallback(() => {
    const { storageKey: key } = optionRef.current;
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }, []);

  /**
   * Core save routine.
   * @param {object} [override]  Explicit data to persist (immediate actions pass
   *                             the freshly-computed state so we don't wait a render).
   * @param {string[]} [onlyFields]  Restrict the diff to these keys (immediate patch).
   */
  const runSave = useCallback(
    async (override, onlyFields) => {
      clearTimers();

      // Rule: never overlap. Queue the newest snapshot for after the in-flight save.
      if (isSavingRef.current) {
        pendingRef.current = true;
        return;
      }

      const full = override || dataRef.current;
      if (override) dataRef.current = full; // keep debounced path in sync

      // Compute changed fields (optionally restricted to an immediate patch).
      let changed = getChangedFields(full);
      if (onlyFields) {
        changed = onlyFields.reduce((acc, k) => {
          if (k in changed) acc[k] = changed[k];
          return acc;
        }, {});
      }

      // Rule: skip duplicates — nothing actually changed.
      if (Object.keys(changed).length === 0) return;

      // Optional guard (e.g. required fields missing).
      if (canSaveRef.current && !canSaveRef.current(full)) return;

      // Offline: don't fire a doomed request; stash locally and wait for reconnect.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        persistLocally(full);
        pendingRef.current = true;
        setStatus("error");
        return;
      }

      // Snapshot the fields we're about to persist so a later success marks the
      // right baseline even if the user keeps typing during the request.
      const { serialize: ser } = optionRef.current;
      const snapshotSerialized = {};
      Object.keys(changed).forEach((k) => {
        snapshotSerialized[k] = ser(full[k]);
      });

      isSavingRef.current = true;
      pendingRef.current = false;
      setStatus(retryCountRef.current > 0 ? "retrying" : "saving");

      try {
        await onSaveRef.current(changed, { full, immediate: Boolean(onlyFields) });
        // Advance the baseline only for the fields we saved.
        savedFieldsRef.current = {
          ...savedFieldsRef.current,
          ...snapshotSerialized,
        };
        retryCountRef.current = 0;
        clearLocal();
        setLastSavedAt(new Date());
        setStatus("saved");
      } catch (error) {
        // Keep the work: baseline untouched, persist locally, schedule a retry.
        console.error("Auto-save failed:", error);
        persistLocally(full);
        pendingRef.current = true;
        retryCountRef.current += 1;
        setStatus("error");
      } finally {
        isSavingRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          // Exponential-ish backoff capped at 15s for retries; snappy otherwise.
          const delay =
            retryCountRef.current > 0
              ? Math.min(1000 * 2 ** (retryCountRef.current - 1), 15000)
              : 300;
          retryTimerRef.current = setTimeout(() => runSave(), delay);
        }
      }
    },
    [clearTimers, getChangedFields, persistLocally, clearLocal]
  );

  // Imperative flush (manual save / lifecycle events). Bypasses the debounce.
  const saveNow = useCallback(() => runSave(), [runSave]);

  /**
   * Immediate save for discrete actions. Optionally merge a patch first so the
   * caller doesn't have to wait for React state to flush.
   * Example: saveImmediately({ title: "New title" }).
   */
  const saveImmediately = useCallback(
    (patch) => {
      if (patch && typeof patch === "object") {
        const merged = { ...dataRef.current, ...patch };
        return runSave(merged, Object.keys(patch));
      }
      return runSave();
    },
    [runSave]
  );

  // Debounced reaction to `data` changes (text typing).
  useEffect(() => {
    dataRef.current = data;
    if (!enabled) return undefined;

    const changed = getChangedFields(data);
    if (Object.keys(changed).length === 0) return undefined; // nothing dirty

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => runSave(), debounceMs);

    // Ceiling: guarantee a save at least every maxWaitMs while typing non-stop.
    if (!maxWaitTimerRef.current) {
      maxWaitTimerRef.current = setTimeout(() => runSave(), maxWaitMs);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled, debounceMs, maxWaitMs]);

  // Track connectivity; flush pending work as soon as the network returns.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => {
      setIsOnline(true);
      if (enabled && pendingRef.current) {
        retryCountRef.current = 0;
        runSave();
      }
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Student-writing lifecycle flushes: page leave / tab hidden / window blur /
  // before unload. These force an immediate flush so nothing is lost.
  useEffect(() => {
    if (!saveOnLifecycle || typeof window === "undefined") return undefined;
    const flush = () => {
      if (enabled) runSave();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("blur", flush);
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("blur", flush);
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveOnLifecycle, enabled]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      clearTimers();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [clearTimers]);

  return {
    status,
    lastSavedAt,
    isOnline,
    hasPendingChanges: pendingRef.current,
    saveNow,
    saveImmediately,
    markSaved,
  };
}
